import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormHelperText,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import { modalStyles, useStyles } from "./AddOrEditUser.styles";
import { AddOrEditMode, IAddOrEditUser, IAddOrEditUserFormValues, IUpsertUserValues } from "./AddOrEditUserInterfaces";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import { Form, Formik } from "formik";
import { UsersEntity } from "entities/Users/UsersEntity";
import { ProductionLinesEntity } from "entities/ProductionLines/ProductionLinesEntity";
import { ClusterEntity } from "entities/ClusterEntity/ClusterEntity";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import { addUserFormValidationSchema } from "validations/schemas";
import { useTranslation } from "react-i18next";
import UserHMIsAccess from "./components/UserHMIsAccess/UserHMIsAccess.view";
import { DefaultTranslationFieldsInTable, USERTYPE } from "constants/constants";
import { UserContext } from "contexts/UserContext/UserContext";
import { uploadUserImage } from "api/queries/usersAPI";
import UserClustersAccess from "./components/UserClustersAccess/UserClustersAccess.view";
import { translateDefaultEntity } from "utilities/localesUtilities";

const AddOrEditUser = ({ open, onClose, onAddUser, onEditUser, user, clusterAllowed }: IAddOrEditUser) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { millId } = useParams();
    const { user_id } = useContext(UserContext);
    const { currentUser } = UsersEntity.GetCurrentUser(Number(user_id));
    const { loading: loadingUserTypes, userTypes } = UsersEntity.GetUserTypes();
    const { productionLines, corporateId } = ProductionLinesEntity.GetProductionLinesAndHMIs(Number(millId));
    const { clusters } = ClusterEntity.GetClusters(Number(millId));
    const [isSaving, setIsSaving] = useState(false);
    const { error: upsertUserError, upsertUserData } = UsersEntity.UpsertUser();
    const [error, setError] = useState("");

    const mode = useMemo(() => (user?.user_id ? AddOrEditMode.edit : AddOrEditMode.add), [user]);
    const [url, setUrl] = useState(user?.media?.media_url || "");

    const [file, setFile] = useState<{
        preview: string;
        data: File;
    } | null>(null);
    const currentUserType = useMemo(() => currentUser?.user_type?.user_type_id, [currentUser]);
    const filteredUserTypesByRole = useMemo(
        () =>
            userTypes.filter((userType) =>
                currentUserType === USERTYPE.ADMINISTRATOR || currentUserType === USERTYPE.OSORNOADMINISTRATOR
                    ? true
                    : userType.user_type_id === USERTYPE.ADMINISTRATOR
                    ? false
                    : true,
            ),
        [userTypes, currentUserType],
    );

    useEffect(() => {
        if (upsertUserError && upsertUserError.message.includes("Uniqueness violation")) {
            setError(t("locations.user_tab.add_user.uniqueness_violation"));
        }
    }, [upsertUserError, t]);

    useEffect(() => {
        if (user?.media?.media_url) {
            setUrl(user.media.media_url);
        }
    }, [user]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const img = {
            preview: URL.createObjectURL(e.target.files[0]),
            data: e.target.files[0],
        };
        setFile(img);
    };

    const handleSubmit = async (values: IAddOrEditUserFormValues) => {
        setIsSaving(true);
        const initialUserMachineCenters = user?.user_machine_centers?.map((userMachineCenter) => {
            return {
                user_machine_center_id: userMachineCenter.user_machine_center_id,
                machine_center_id: userMachineCenter.machine_center.machine_center_id,
                is_enabled: userMachineCenter.is_enabled,
            };
        });

        const newMachineCenterIds = [] as {
            user_machine_center_id?: number;
            machine_center_id: number;
            is_enabled: boolean;
        }[];

        Object.keys(values.userMachineCenters).forEach((productionLineId) => {
            values.userMachineCenters[productionLineId].forEach((machineCenterId) => {
                newMachineCenterIds.push({
                    machine_center_id: machineCenterId,
                    is_enabled: true,
                });
            });
        });

        initialUserMachineCenters?.forEach((initialUserMachineCenter) => {
            const found = newMachineCenterIds.find(
                (newMachineCenterId) =>
                    newMachineCenterId.machine_center_id === initialUserMachineCenter.machine_center_id,
            );
            if (!found) {
                newMachineCenterIds.push({
                    user_machine_center_id: initialUserMachineCenter.user_machine_center_id,
                    machine_center_id: initialUserMachineCenter.machine_center_id,
                    is_enabled: false,
                });
            }
        });

        const upsertUserValues: IUpsertUserValues = {
            ...(user?.user_id && { user_id: user.user_id }),
            corporate_id: corporateId,
            ...(values.userTypeId !== USERTYPE.ADMINISTRATOR && { mill_id: Number(millId) }),
            user_type_id: values.userTypeId,
            first_name: values.firstname,
            last_name: values.lastname,
            employee_number: values.employeeNumber,
            email: values.email ? values.email : null,
            user_machine_centers: {
                data: newMachineCenterIds,
                on_conflict: {
                    constraint: "user_machine_center_user_id_machine_center_id_key",
                    update_columns: ["is_enabled"],
                },
            },
        };

        if (clusterAllowed) {
            const initialUserClusters = user?.user_clusters?.map((userCluster) => {
                return {
                    user_cluster_id: userCluster.user_cluster_id,
                    cluster_id: userCluster.cluster.cluster_id,
                    is_enabled: userCluster.is_enabled,
                };
            });

            const clustersIds = [] as {
                user_cluster_id?: number;
                cluster_id: number;
                is_enabled: boolean;
            }[];

            values.userClusters.forEach((clusterId) => {
                clustersIds.push({
                    cluster_id: clusterId,
                    is_enabled: true,
                });
            });

            initialUserClusters?.forEach((initialUserCluster) => {
                const found = clustersIds.find((cluster) => cluster.cluster_id === initialUserCluster.cluster_id);

                if (!found) {
                    clustersIds.push({
                        user_cluster_id: initialUserCluster.user_cluster_id,
                        cluster_id: initialUserCluster.cluster_id,
                        is_enabled: false,
                    });
                }
            });

            upsertUserValues.user_clusters = {
                data: clustersIds,
                on_conflict: {
                    constraint: "user_cluster_cluster_id_user_id_key",
                    update_columns: ["is_enabled"],
                },
            };
        }

        try {
            const userUpsertResponse = await upsertUserData({ variables: { user: upsertUserValues } });
            if (userUpsertResponse) {
                if (file && file.data) {
                    const formData = new FormData();
                    formData.set("file", file.data as File);
                    formData.set("user_id", userUpsertResponse.data?.insert_user?.returning[0].user_id);
                    await uploadUserImage(userUpsertResponse.data?.insert_user?.returning[0].user_id, formData);
                }
                setIsSaving(false);
                if (user?.user_id) {
                    setFile(null);
                    setUrl("");
                    onEditUser();
                } else {
                    setFile(null);
                    setUrl("");
                    onAddUser();
                }
            }
        } catch {
            setIsSaving(false);
            return;
        }
    };

    const handleClose = () => {
        setFile(null);
        setUrl("");
        onClose();
    };

    const initFormValues: IAddOrEditUserFormValues = {
        firstname: user?.first_name || "",
        lastname: user?.last_name || "",
        employeeNumber: user?.employee_number || "",
        email: user?.email || "",
        userTypeId: user?.user_type.user_type_id || userTypes[0]?.user_type_id || 0,
        userMachineCenters: {},
        userClusters: [],
    };

    return (
        <Modal open={open} onClose={handleClose} disableEscapeKeyDown>
            <Box sx={modalStyles}>
                <Snackbar
                    open={Boolean(error)}
                    onClose={() => setError("")}
                    autoHideDuration={6000}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Alert severity="error" sx={{ width: "100%", marginBottom: 6 }}>
                        {error}
                    </Alert>
                </Snackbar>
                <Formik
                    initialValues={initFormValues}
                    validationSchema={addUserFormValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                        return (
                            <Form>
                                <div className={classes.modalHeader}>
                                    <p>
                                        {mode === AddOrEditMode.add
                                            ? t("locations.user_tab.add_user.header")
                                            : t("locations.user_tab.edit_user.header")}
                                    </p>
                                </div>
                                <div className={classes.modalBody}>
                                    <div className={classes.section}>
                                        <div className={classes.modalBodyLeft}>
                                            <div className={classes.userImageWrapper}>
                                                {url || file ? (
                                                    <div>
                                                        <img
                                                            src={file?.preview || url}
                                                            alt="user"
                                                            className={classes.userImage}
                                                        />
                                                    </div>
                                                ) : (
                                                    <CropOriginalIcon className={classes.userImageIcon} />
                                                )}
                                            </div>
                                            <span className={classes.userImageActionWrapper}>
                                                <Button
                                                    disabled={isSaving}
                                                    variant="text"
                                                    component="label"
                                                    color="info"
                                                    className={classes.userImageAction}
                                                >
                                                    {t("locations.user_tab.add_user.upload_image")}
                                                    <input
                                                        hidden
                                                        accept="image/*"
                                                        multiple
                                                        type="file"
                                                        onChange={handleFileChange}
                                                    />
                                                </Button>
                                            </span>
                                        </div>
                                        <div className={classes.modalBodyRight}>
                                            <div className={classes.formRow}>
                                                <Box className={classes.boxContainer}>
                                                    <InputLabel id="outlined-name-label">
                                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                                            {t("locations.user_tab.add_user.first_name")}*
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        disabled={isSaving}
                                                        aria-labelledby="outlined-firstname-label"
                                                        fullWidth
                                                        id="outlined-firstname-input"
                                                        className={classes.textField}
                                                        name="firstname"
                                                        type="text"
                                                        variant="outlined"
                                                        value={values.firstname}
                                                        onChange={handleChange}
                                                        error={touched.firstname && Boolean(errors.firstname)}
                                                        helperText={
                                                            touched.firstname && errors.firstname && t(errors.firstname)
                                                        }
                                                    />
                                                </Box>
                                                <Box className={classes.boxContainer}>
                                                    <InputLabel id="outlined-name-label">
                                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                                            {t("locations.user_tab.add_user.last_name")}*
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        disabled={isSaving}
                                                        aria-labelledby="outlined-lastname-label"
                                                        fullWidth
                                                        id="outlined-lastname-input"
                                                        className={classes.textField}
                                                        name="lastname"
                                                        type="text"
                                                        variant="outlined"
                                                        value={values.lastname}
                                                        onChange={handleChange}
                                                        error={touched.lastname && Boolean(errors.lastname)}
                                                        helperText={
                                                            touched.lastname && errors.lastname && t(errors.lastname)
                                                        }
                                                    />
                                                </Box>
                                            </div>
                                            <Box className={classes.boxContainer}>
                                                <InputLabel id="outlined-employeeNumber-label">
                                                    <Typography variant="subtitle2" className={classes.formLabel}>
                                                        {t("locations.user_tab.add_user.employee_number")}
                                                    </Typography>
                                                </InputLabel>
                                                <TextField
                                                    disabled={isSaving}
                                                    aria-labelledby="outlined-employeeNumber-label"
                                                    fullWidth
                                                    id="outlined-employeeNumber-input"
                                                    className={classes.textField}
                                                    name="employeeNumber"
                                                    type="text"
                                                    variant="outlined"
                                                    value={values.employeeNumber}
                                                    onChange={handleChange}
                                                    error={touched.employeeNumber && Boolean(errors.employeeNumber)}
                                                    helperText={
                                                        touched.employeeNumber &&
                                                        errors.employeeNumber &&
                                                        t(errors.employeeNumber)
                                                    }
                                                />
                                            </Box>
                                            <Box className={classes.boxContainer}>
                                                <InputLabel id="outlined-email-label">
                                                    <Typography variant="subtitle2" className={classes.formLabel}>
                                                        {t("locations.user_tab.add_user.email")}
                                                        {values.userTypeId !== USERTYPE.OPERATOR && "*"}
                                                    </Typography>
                                                </InputLabel>
                                                <TextField
                                                    aria-labelledby="outlined-email-label"
                                                    fullWidth
                                                    id="outlined-email-input"
                                                    className={classes.textField}
                                                    name="email"
                                                    type="text"
                                                    variant="outlined"
                                                    disabled={mode === AddOrEditMode.edit || isSaving}
                                                    value={values.email}
                                                    onChange={handleChange}
                                                    error={touched.email && Boolean(errors.email)}
                                                    helperText={touched.email && errors.email && t(errors.email)}
                                                />
                                            </Box>
                                        </div>
                                    </div>
                                    <Divider className={classes.divider} />
                                    <div className={classes.section}>
                                        <div className={classes.sectionContent}>
                                            <Box className={classes.boxContainer}>
                                                <InputLabel id="userTypeId-select-label">
                                                    <Typography variant="subtitle2" className={classes.formLabel}>
                                                        {t("locations.user_tab.add_user.user_type")}
                                                    </Typography>
                                                </InputLabel>
                                                {loadingUserTypes ? (
                                                    <Box sx={{ display: "flex" }}>
                                                        <CircularProgress />
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <Select
                                                            disabled={isSaving}
                                                            fullWidth
                                                            labelId="userTypeId-select-label"
                                                            name="userTypeId"
                                                            value={values.userTypeId}
                                                            onChange={(e) => {
                                                                if (e.target.value !== USERTYPE.OPERATOR) {
                                                                    setFieldValue("userMachineCenters", {});
                                                                }
                                                                handleChange(e);
                                                            }}
                                                            className={classes.select}
                                                            error={!!errors.userTypeId && !!touched.userTypeId}
                                                        >
                                                            {filteredUserTypesByRole.map((userType) => (
                                                                <MenuItem
                                                                    key={userType.user_type_id}
                                                                    value={userType.user_type_id}
                                                                >
                                                                    {translateDefaultEntity(
                                                                        i18n.language,
                                                                        userType,
                                                                        "name",
                                                                        DefaultTranslationFieldsInTable.USER_TYPE,
                                                                    )}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {!!errors.userTypeId && !!touched.userTypeId && (
                                                            <FormHelperText>{t(errors.userTypeId)}</FormHelperText>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                            {values.userTypeId === USERTYPE.OPERATOR && (
                                                <>
                                                    {clusterAllowed && clusters.length > 0 && (
                                                        <UserClustersAccess
                                                            disabled={isSaving}
                                                            clusters={clusters}
                                                            setFieldValue={setFieldValue}
                                                            values={values}
                                                            user={user}
                                                        />
                                                    )}
                                                    <UserHMIsAccess
                                                        disabled={isSaving}
                                                        productionLines={productionLines}
                                                        setFieldValue={setFieldValue}
                                                        values={values}
                                                        user={user}
                                                        clusterAllowed={clusterAllowed}
                                                    />
                                                </>
                                            )}
                                            {values.userTypeId !== USERTYPE.OPERATOR && mode === AddOrEditMode.add && (
                                                <div>
                                                    <Alert severity="info">
                                                        {t("locations.user_tab.add_user.password_instructions")}
                                                    </Alert>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={classes.footer}>
                                    <Button
                                        variant="text"
                                        color="info"
                                        className={classes.button}
                                        onClick={handleClose}
                                        disabled={isSubmitting || isSaving}
                                    >
                                        {t("locations.user_tab.add_user.cancel")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="info"
                                        className={classes.submitButton}
                                        type="submit"
                                        disabled={isSubmitting || isSaving}
                                    >
                                        {!!(mode === AddOrEditMode.add) && <AddIcon />}
                                        {mode === AddOrEditMode.add
                                            ? t("locations.user_tab.add_user.title")
                                            : t("locations.user_tab.edit_user.save")}
                                    </Button>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </Box>
        </Modal>
    );
};

export default AddOrEditUser;
